import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEraser } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArrayHelper } from 'src/app/helpers/array-helper';
import { DateHelper } from 'src/app/helpers/date-helper';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { TChatStateService } from 'src/app/services/state/tchat-state.service';
import { FormFocusDirective } from '../../../core/directives/form-focus.directive';
import { TchatRoom } from '../../../models/server/tchat-room';
import { TchatUserIdentityComponent } from '../tchat-user-identity/tchat-user-identity.component';

@Component({
    selector: 'app-tchat-rooms',
    templateUrl: './tchat-rooms.component.html',
    styleUrls: ['./tchat-rooms.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatIconModule,
        NgClass,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        TchatUserIdentityComponent,
        DatePipe,
    ]
})
export class TchatRoomsComponent implements OnInit, OnDestroy {
    /**
     * Références des constantes utilisées dans le template.
     */
    public FRENCH_DATE_FORMAT = DateHelper.FRENCH_DATE_FORMAT;

    public resetFilterIcon = faEraser;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Emis lorsqu'une conversation est sélectionnée.
     */
    @Output()
    selectionChanged: EventEmitter<TchatRoom> = new EventEmitter<TchatRoom>();

    /**
     * Liste de toutes les conversations de l'utilisateur.
     */
    public rawRooms: TchatRoom[];

    /**
     * Liste de toutes les conversations de l'utilisateur respectant les filtres.
     */
    public filteredRooms: TchatRoom[];

    /**
     * Conversation selectionnée.
     */
    public selectedRoom: TchatRoom = null;

    /**
     * Formulaire contenant les filtres du tableau de résultats.
     */
    public filtersForm: UntypedFormGroup;

    /**
     * Constructeur.
     * @param tchatService
     * @param connectedUserStateService
     * @param cdRef
     */
    constructor(
        protected tchatService: TChatStateService,
        protected connectedUserStateService: ConnectedUsersStateService,
        private cdRef: ChangeDetectorRef
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.filtersForm = new UntypedFormGroup({});
        this.filtersForm.addControl('searchControlName', new UntypedFormControl());

        this.subscribeToRoomChangeEvents();

        this.subscribeToNotificationEvents();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Selectionne un contact
     * @param roomSelected contact sélectionne=é
     */
    public onSelected(roomSelected: TchatRoom) {
        this.selectedRoom = roomSelected;
        this.selectionChanged.emit(roomSelected);
    }

    /**
     * Recherche des contacts
     */
    public onSearch(): void {
        let currentFilterValue = this.filtersForm.controls['searchControlName'].value;

        if (!!currentFilterValue) {
            const datas = this.rawRooms.slice();
            this.filteredRooms = datas.filter((x) => x.target.displayName.toLowerCase().includes(currentFilterValue.toLowerCase()));
        } else {
            this.resetFilter();
        }

        this.cdRef.detectChanges();
    }

    /**
     * Reset le filtre
     */
    public resetFilter(): void {
        this.filteredRooms = this.rawRooms.slice();
        this.filtersForm.controls['searchControlName'].setValue(null);
    }

    /**
     * S'abonne aux événements de changement de conversation.
     */
    private subscribeToRoomChangeEvents() {
        this.tchatService.rooms$.pipe(takeUntil(this.componentDestroyed$)).subscribe((rooms) => {
            this.rawRooms = [];

            rooms.forEach((x) => {
                this.rawRooms.push(x);
            });

            this.filteredRooms = this.rawRooms.slice();
        });
    }

    /**
     * S'abonne aux événements de notification.
     */
    private subscribeToNotificationEvents() {
        this.tchatService.notifications$.pipe(takeUntil(this.componentDestroyed$)).subscribe((notifications) => {
            this.rawRooms.forEach((r) => {
                let notification = notifications.find((n) =>
                    ArrayHelper.arrayIsEqual(
                        n.users.map((x) => x.login),
                        r.users.map((x) => x.login)
                    )
                );
                r.notifications = !!notification ? notification.notification : 0;
            });
            this.filteredRooms = this.rawRooms.slice();
            this.cdRef.detectChanges();
        });
    }

    /**
     * Retourne la date courante.
     * @returns La date du jour.
     */
    public getCurrentDate() {
        return new Date();
    }
}
